import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Clock, Calendar, Building2, Sparkles, ListChecks } from 'lucide-react';

/**
 * Sprint CMA-C — Climara anonymous-response intro gate.
 *
 * Rendered ONLY when the candidate opens a Climara assignment, before the
 * shared questionnaire renderer starts. Pure presentational — no API calls,
 * no data mutation. The shared assignment + answer storage stays unchanged.
 */
export default function ClimaraIntroGate({
  assignment,
  questionnaire,
  companyName,
  estimatedMinutes,
  onStart,
}) {
  const { t, i18n } = useTranslation();
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  const tx = (es, en) => (isEN && en ? en : (es || en || ''));

  const dueDate = assignment?.deadline
    ? new Date(assignment.deadline).toLocaleDateString(isEN ? 'en-US' : 'es-ES', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;

  const name = tx(questionnaire?.name, questionnaire?.name)
    || assignment?.title
    || (isEN ? 'Climara survey' : 'Encuesta Climara');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-6">
      <Card
        className="max-w-xl w-full p-8 lg:p-10 shadow-elegant"
        data-testid="climara-intro-gate"
      >
        <div className="flex items-center gap-3 mb-5">
          <span className="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-teal-400/20 to-cyan-500/10">
            <Sparkles className="h-5 w-5 text-cyan-700" />
          </span>
          <div>
            <Badge variant="outline" className="text-xs uppercase tracking-wider">
              {t('climara.intro.badge', { defaultValue: 'Climara · Climate survey' })}
            </Badge>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="climara-intro-title">
              {name}
            </h1>
          </div>
        </div>

        <div className="space-y-3 mb-6 text-sm">
          {companyName && (
            <div className="flex items-center gap-3 text-foreground/85" data-testid="climara-intro-company">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span>{companyName}</span>
            </div>
          )}
          <div className="flex items-center gap-3 text-foreground/85" data-testid="climara-intro-duration">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>
              {t('climara.intro.duration', {
                defaultValue: 'Estimated time: about {{n}} minutes',
                n: estimatedMinutes,
              })}
            </span>
          </div>
          {dueDate && (
            <div className="flex items-center gap-3 text-foreground/85" data-testid="climara-intro-deadline">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{t('climara.intro.due', { defaultValue: 'Due by' })}: {dueDate}</span>
            </div>
          )}
        </div>

        <div
          className="rounded-xl border border-emerald-200/70 bg-emerald-50/60 px-4 py-4 mb-4"
          data-testid="climara-intro-anonymity"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-900">
                {t('climara.intro.anonHeader', { defaultValue: 'Your responses are anonymous.' })}
              </p>
              <p className="text-sm text-emerald-900/85 mt-1 leading-relaxed">
                {t('climara.intro.anonBody', {
                  defaultValue:
                    'Neither your manager nor your company will see your individual answers. Only consolidated organizational results will be reported.',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Founder UAT iter 2 — best-practice recommendations before starting */}
        <div
          className="rounded-xl border border-sky-200/70 bg-sky-50/50 px-4 py-4 mb-6"
          data-testid="climara-intro-tips"
        >
          <div className="flex items-start gap-3">
            <ListChecks className="h-5 w-5 text-sky-700 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-sky-900">
                {t('climara.intro.tipsHeader', { defaultValue: 'Before you start' })}
              </p>
              <ul className="text-sm text-sky-900/85 mt-1.5 space-y-1 leading-relaxed list-disc pl-4">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <li key={n} data-testid={`climara-intro-tip-${n}`}>
                    {t(`climara.intro.tip${n}`, { defaultValue: '' })}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <Button
          onClick={onStart}
          className="w-full bg-gradient-primary hover:opacity-90 h-11"
          data-testid="climara-intro-start"
        >
          {t('climara.intro.start', { defaultValue: 'Start the survey' })}
        </Button>
      </Card>
    </div>
  );
}
